import React, { useCallback, useRef, useState } from 'react';
import { Typography, Badge } from 'antd';
import {
    MobileOutlined,
    TabletOutlined,
    DesktopOutlined
} from '@ant-design/icons';
import { useDrop } from 'react-dnd';
import UIComponentWrapper from './ui-kit/UIComponentWrapper';
import UIComponentRenderer from './ui-kit/UIComponentRenderer';
import AlignmentGuides from './AlignmentGuides';
import { calculateSnapPosition } from '../utils/snapUtils';

const { Text } = Typography;

// Device icons mapping
const DEVICE_ICONS = {
    mobile: <MobileOutlined />,
    tablet: <TabletOutlined />,
    desktop: <DesktopOutlined />
};

/**
 * Artboard component for UI Design Canvas
 * Represents a device-specific artboard (mobile, tablet, or desktop)
 * Supports snap-to-edge alignment with visual guides
 */
const Artboard = ({
    artboard,
    components = [],
    isActive,
    showGrid,
    selectedComponentIds = new Set(),
    onArtboardClick,
    onComponentSelect,
    onComponentMove,
    onGroupMove,
    onComponentResize,
    onComponentDrop,
    onArtboardMove,
    currentOffset = { x: 0, y: 0 },
    designKit
}) => {
    const artboardRef = useRef(null);
    const isDraggingRef = useRef(false); // Track if we're dragging to prevent click handler
    const [isDragOver, setIsDragOver] = useState(false);
    const [alignmentGuides, setAlignmentGuides] = useState({ vertical: [], horizontal: [] });
    const [groupDragOffset, setGroupDragOffset] = useState(null); // { draggingId, deltaX, deltaY }

    // Handle drop from design kit
    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: 'DESIGN_KIT_COMPONENT',
        drop: (item, monitor) => {
            const offset = monitor.getClientOffset();
            const artboardRect = artboardRef.current?.getBoundingClientRect();

            if (offset && artboardRect) {
                // Calculate position relative to artboard
                const x = offset.x - artboardRect.left;
                const y = offset.y - artboardRect.top;
                onComponentDrop(item.componentType, { x, y });
            }
            setAlignmentGuides({ vertical: [], horizontal: [] });
            return { dropped: true };
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
        })
    }), [onComponentDrop]);

    // Combine refs
    const setRefs = useCallback((node) => {
        artboardRef.current = node;
        drop(node);
    }, [drop]);

    // Handle component drag with snap detection
    const handleComponentDrag = useCallback((componentId, position, dimensions) => {
        const { position: snappedPosition, guides } = calculateSnapPosition(
            position,
            dimensions,
            components,
            artboard.width,
            artboard.height,
            componentId
        );

        setAlignmentGuides(guides);
        return snappedPosition;
    }, [components, artboard.width, artboard.height]);

    // Clear alignment guides when drag ends
    const handleDragEnd = useCallback(() => {
        setAlignmentGuides({ vertical: [], horizontal: [] });
    }, []);

    // Handle group drag offset during drag (for smooth visual feedback)
    const handleGroupDrag = useCallback((draggingId, delta) => {
        setGroupDragOffset({ draggingId, deltaX: delta.x, deltaY: delta.y });
    }, []);

    // Clear group drag offset on drag end
    const handleGroupDragEnd = useCallback(() => {
        setGroupDragOffset(null);
    }, []);

    // Grid pattern for guides
    const gridPattern = showGrid ? `
        repeating-linear-gradient(
            0deg,
            transparent,
            transparent 19px,
            rgba(88, 166, 255, 0.05) 19px,
            rgba(88, 166, 255, 0.05) 20px
        ),
        repeating-linear-gradient(
            90deg,
            transparent,
            transparent 19px,
            rgba(88, 166, 255, 0.05) 19px,
            rgba(88, 166, 255, 0.05) 20px
        )
    ` : 'none';

    // Handle artboard header drag - direct DOM manipulation for 60fps performance
    const handleHeaderMouseDown = useCallback((e) => {
        if (!onArtboardMove) return;

        e.stopPropagation();
        e.preventDefault();

        const startX = e.clientX;
        const startY = e.clientY;
        const startOffsetX = currentOffset.x;
        const startOffsetY = currentOffset.y;

        // Get the wrapper element for direct DOM manipulation
        const wrapperElement = e.currentTarget.closest('[data-artboard-wrapper]');
        if (!wrapperElement) return;

        // Get the current zoom scale from the TransformComponent
        // TransformComponent applies a transform: matrix(scale, 0, 0, scale, x, y)
        const transformComponent = wrapperElement.closest('.react-transform-component');
        let scale = 1;
        if (transformComponent) {
            const style = window.getComputedStyle(transformComponent);
            const transform = style.transform;
            if (transform && transform !== 'none') {
                // Parse scale from matrix(a, b, c, d, tx, ty) where a = scale
                const match = transform.match(/matrix\(([^,]+)/);
                if (match) {
                    scale = parseFloat(match[1]) || 1;
                }
            }
        }

        // Track current offset during drag
        let currentX = startOffsetX;
        let currentY = startOffsetY;
        let hasMoved = false;

        // Mark as dragging to prevent click handler
        isDraggingRef.current = true;

        // Change cursor to grabbing
        document.body.style.cursor = 'grabbing';

        const handleMouseMove = (moveEvent) => {
            moveEvent.preventDefault();
            // Divide delta by scale to compensate for zoom
            const deltaX = (moveEvent.clientX - startX) / scale;
            const deltaY = (moveEvent.clientY - startY) / scale;

            // Only consider it a move if we've actually moved more than a few pixels
            if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
                hasMoved = true;
            }

            currentX = startOffsetX + deltaX;
            currentY = startOffsetY + deltaY;

            // Direct DOM manipulation for immediate visual feedback (no React re-render)
            wrapperElement.style.transform = `translate(${currentX}px, ${currentY}px)`;
            wrapperElement.style.zIndex = '10';
        };

        const handleMouseUp = () => {
            document.body.style.cursor = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);

            // Only modify styles and state if user actually dragged (not just clicked)
            if (hasMoved) {
                // Clear inline styles so React state controls the position
                wrapperElement.style.transform = '';
                wrapperElement.style.zIndex = '';
                // Update React state (persists the position)
                onArtboardMove({ x: currentX, y: currentY });
            }

            // Reset dragging flag after a short delay to allow click event to be blocked
            setTimeout(() => {
                isDraggingRef.current = false;
            }, 50);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [onArtboardMove, currentOffset]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}
        >
            {/* Artboard Header - Draggable */}
            <div
                onMouseDown={handleHeaderMouseDown}
                onClick={(e) => {
                    // Skip click if we just finished a drag
                    if (isDraggingRef.current) return;
                    onArtboardClick();
                }}
                onDoubleClick={() => {
                    // Zoom to fit on double-click
                    console.log('Zoom to fit:', artboard.device_type);
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    marginBottom: 12,
                    background: isActive ? 'rgba(88, 166, 255, 0.1)' : 'transparent',
                    border: isActive ? '1px solid #58a6ff' : '1px solid transparent',
                    borderRadius: 6,
                    cursor: 'grab',
                    transition: 'all 0.2s ease',
                    userSelect: 'none'
                }}
            >
                <span style={{ color: isActive ? '#58a6ff' : '#8b949e' }}>
                    {DEVICE_ICONS[artboard.device_type]}
                </span>
                <Text style={{
                    color: isActive ? '#c9d1d9' : '#8b949e',
                    fontWeight: isActive ? 600 : 400
                }}>
                    {artboard.name || artboard.device_type}
                </Text>
                <Text style={{ color: '#484f58', fontSize: 12 }}>
                    {artboard.width} × {artboard.height}
                </Text>
                {components.length > 0 && (
                    <Badge
                        count={components.length}
                        style={{
                            backgroundColor: '#238636',
                            fontSize: 10
                        }}
                    />
                )}
            </div>

            {/* Artboard Canvas */}
            <div
                ref={setRefs}
                onClick={(e) => {
                    // Click on empty area deselects
                    if (e.target === e.currentTarget) {
                        onComponentSelect(null, e);
                    }
                    onArtboardClick();
                }}
                style={{
                    width: artboard.width,
                    height: artboard.height,
                    background: isOver && canDrop
                        ? 'rgba(88, 166, 255, 0.05)'
                        : '#1c2128',
                    border: isActive
                        ? '2px solid #58a6ff'
                        : isOver && canDrop
                            ? '2px dashed #58a6ff'
                            : '1px solid #30363d',
                    borderRadius: 8,
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundImage: gridPattern,
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                    boxShadow: isActive
                        ? '0 0 20px rgba(88, 166, 255, 0.15)'
                        : '0 4px 12px rgba(0, 0, 0, 0.3)'
                }}
            >
                {/* Drop hint overlay */}
                {isOver && canDrop && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(88, 166, 255, 0.1)',
                        zIndex: 100,
                        pointerEvents: 'none'
                    }}>
                        <Text style={{ color: '#58a6ff', fontSize: 16 }}>
                            Drop component here
                        </Text>
                    </div>
                )}

                {/* Alignment Guides */}
                <AlignmentGuides
                    guides={alignmentGuides}
                    artboardWidth={artboard.width}
                    artboardHeight={artboard.height}
                />

                {/* Render components */}
                {components.sort((a, b) => (a.z_index || 1) - (b.z_index || 1)).map(component => (
                    <UIComponentWrapper
                        key={component.id}
                        component={component}
                        isSelected={selectedComponentIds.has(component.id)}
                        onSelect={(e) => onComponentSelect(component.id, e)}
                        onMove={(position) => onComponentMove(component.id, position)}
                        onResize={(dimensions) => onComponentResize(component.id, dimensions)}
                        artboardWidth={artboard.width}
                        artboardHeight={artboard.height}
                        siblingComponents={components}
                        selectedComponentIds={selectedComponentIds}
                        groupDragOffset={groupDragOffset}
                        onGroupMove={onGroupMove}
                        onGroupDrag={handleGroupDrag}
                        onGroupDragEnd={handleGroupDragEnd}
                        onDrag={handleComponentDrag}
                        onDragEnd={handleDragEnd}
                    >
                        <UIComponentRenderer
                            componentType={component.component_type}
                            state={component.state}
                            contentOverrides={component.content_overrides}
                            deviceType={artboard.device_type}
                            designKit={designKit}
                        />
                    </UIComponentWrapper>
                ))}
            </div>
        </div>
    );
};

export default Artboard;
